import React from 'react';
import { useEffect } from 'react';
import { Button, Grid, Typography } from '@mui/material';
import CustomTextField from '../formFields/textField';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { PESSENGER_RADIO } from 'src/constants';
import LoaderButton from '../loaderButton/loaderButton';
import { bookingFormScheme } from '../yup/scheme';
import moment from 'moment';
import CustomRadioField from '../formFields/radioField';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { USER_ROLES } from 'src/config/enum';
import { calculatePrice } from 'src/helper';

const BookingForm = ({ onSubmit, data, distance, priceData, loading, coordinates, disabled }) => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(bookingFormScheme),
    defaultValues: {
      ...data,
      distance: distance,
      lessThanThreeItems: data.lessThanThreeItems ? data.lessThanThreeItems : 'no',
    },
  });
  useEffect(() => {
    reset({ ...data, dateTime: moment(data.dateTime).format('YYYY-MM-DDTHH:mm') });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      [data.startPoint, ...data.middlePoints, data.endPoint].length !== coordinates.length ||
      data.lessThanThreeItems !== getValues().lessThanThreeItems ||
      data.distance !== distance
    ) {
      if (priceData) {
        reset({
          ...getValues(),
          distance,
          price: calculatePrice(
            distance,
            priceData,
            getValues().acceptMarketing,
            getValues().lessThanThreeItems,
            coordinates.length
          ),
        });
      }
    } else {
      reset({
        ...getValues(),
        price: user.role === USER_ROLES.ADMIN ? data.price : ((data.price / 100) * user.jobPercentage).toFixed(2),
        distance: data.distance,
      });
    }

    // eslint-disable-next-line
  }, [distance, watch('lessThanThreeItems')]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography color={'primary'}>Personal Details</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <CustomTextField
            label={'First name'}
            disabled={disabled}
            controller={{
              name: 'firstName',
              control: control,
              errors: errors.firstName,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomTextField
            label={'Last name'}
            disabled={disabled}
            controller={{
              name: 'lastName',
              control: control,
              errors: errors.lastName,
            }}
          />
        </Grid>

        {user.role === USER_ROLES.ADMIN && (
          <>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                label={'Email'}
                type="email"
                controller={{
                  name: 'email',
                  control: control,
                  errors: errors.email,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                label={'Phone number'}
                controller={{
                  name: 'phoneNumber',
                  control: control,
                  errors: errors.phoneNumber,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                label={'Date/Time'}
                disabled={disabled}
                type="datetime-local"
                controller={{
                  name: 'dateTime',
                  control: control,
                  errors: errors.dateTime,
                }}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} sm={3}>
          <CustomTextField
            label={'Price (£)'}
            type="number"
            disabled
            controller={{
              name: 'price',
              control: control,
              errors: errors.price,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <CustomTextField
            label={'Distance (miles)'}
            disabled
            value={distance}
            type="number"
            controller={{
              name: 'distance',
              control: control,
              errors: errors.distance,
            }}
          />
        </Grid>
      </Grid>

      <Grid sx={{ my: 2 }}>
        <Typography sx={{ my: 1 }} color={'primary'}>
          Item Details
        </Typography>
        <CustomTextField
          multiline
          disabled={disabled}
          minRows={4}
          controller={{
            name: 'itemDescription',
            control: control,
            errors: errors.itemDescription,
          }}
        />
      </Grid>

      <Grid sx={{ my: 2 }}>
        <Typography sx={{ my: 1 }} color={'primary'}>
          Other Information
        </Typography>
        <CustomTextField
          required={false}
          multiline
          disabled={disabled}
          minRows={4}
          controller={{
            name: 'otherInformation',
            control: control,
            errors: errors.otherInformation,
          }}
        />
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <CustomRadioField
            label={'People to transport'}
            disabled={disabled}
            radioArray={PESSENGER_RADIO}
            controller={{
              name: 'peopleInVan',
              control: control,
              errors: errors.peopleInVan,
            }}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <CustomTextField value={data?.hearAboutUs} disabled label={'Learnt about us'} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <CustomTextField
            value={data?.acceptMarketing === true ? 'True' : 'False'}
            disabled
            label={'Accept marketing'}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomRadioField
            label={'Less than three items '}
            disabled={disabled}
            radioArray={[
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ]}
            controller={{
              name: 'lessThanThreeItems',
              control: control,
              errors: errors.lessThanThreeItems,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography color={'primary'}>Internal Notes</Typography>
          <CustomTextField
            disabled={disabled}
            required={false}
            multiline
            minRows={4}
            controller={{
              name: 'internalNotes',
              control: control,
              errors: errors.internalNotes,
            }}
          />
        </Grid>
        <Grid item>
          <Button sx={{ mr: 1 }} variant="outlined" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          {user.role === USER_ROLES.ADMIN && (
            <>
              <LoaderButton loading={loading} buttonText={'Save/Close'} type="submit" />
            </>
          )}
        </Grid>
      </Grid>
    </form>
  );
};

export default BookingForm;
